import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { QuoteIndexableData } from 'state/quotes/dashboard/selectors';
import type { GroupColor } from 'state/quotes/dashboard/tiles/tiles.model';

import type { FiltersCounts, PinnedGroupColors } from './Filter.helper';
import { FilterItem } from './FilterItem';

export interface FilterByPinnedProps {
  selectedPinnedGroup?: string;
  filtersCounts: FiltersCounts;
  quotes: QuoteIndexableData[];
  pinnedGroupColors: PinnedGroupColors;
  onByPinnedFilterChanged(pinnedGroup?: string): void;
  onPinnedGroupRenamed(previousGroupName: string, newGroupName: string): void;
  onPinnedGroupColorChanged: (pinnedGroup: string, color?: GroupColor) => void;
  onTilesDeleted(quoteIds: string[]): void;
}

export const FilterByPinned: FC<FilterByPinnedProps> = ({
  selectedPinnedGroup,
  filtersCounts,
  quotes,
  pinnedGroupColors,
  onByPinnedFilterChanged,
  onPinnedGroupRenamed,
  onPinnedGroupColorChanged,
  onTilesDeleted,
}) => {
  const { formatMessage } = useIntl();

  const allFilterLabel = useMemo(() => formatMessage({ id: 'dashboard.filter.pinned.all' }), [formatMessage]);

  const pinnedGroups = useMemo(
    () => Object.keys(filtersCounts.byPinned.byGroup).sort((group1, group2) => group1.localeCompare(group2)),
    [filtersCounts.byPinned.byGroup],
  );

  const onAllPinnedTilesSelected = useCallback(() => {
    onByPinnedFilterChanged();
  }, [onByPinnedFilterChanged]);

  const onAllPinnedTilesDeleted = useCallback(() => {
    const quoteIdsToDelete = quotes.filter(quote => quote.isPinned || !!quote.customGroup).map(quote => quote.quoteId);
    onTilesDeleted(quoteIdsToDelete);
  }, [onTilesDeleted, quotes]);

  const onPinnedGroupTilesDeleted = useCallback(
    (pinnedGroup: string) => {
      const quoteIdsToDelete = quotes.filter(quote => quote.customGroup === pinnedGroup).map(quote => quote.quoteId);
      onTilesDeleted(quoteIdsToDelete);
    },
    [onTilesDeleted, quotes],
  );

  return (
    <ul className="nav nav-underline flex-column nav-hover">
      <FilterItem
        filter="all"
        isActive={!selectedPinnedGroup}
        count={filtersCounts.byPinned.all}
        e2e="pinned"
        label={allFilterLabel}
        icon="push_pin"
        onSelected={onAllPinnedTilesSelected}
        onTilesDeleted={onAllPinnedTilesDeleted}
      />
      {pinnedGroups.map(pinnedGroup => (
        <FilterItem
          key={pinnedGroup}
          filter={pinnedGroup}
          isActive={pinnedGroup === selectedPinnedGroup}
          count={filtersCounts.byPinned.byGroup[pinnedGroup] || 0}
          selectedColor={pinnedGroupColors[pinnedGroup]}
          e2e="pinned-group"
          onSelected={onByPinnedFilterChanged}
          onTilesDeleted={onPinnedGroupTilesDeleted}
          onRenamed={onPinnedGroupRenamed}
          onColorSelected={onPinnedGroupColorChanged}
        />
      ))}
    </ul>
  );
};

FilterByPinned.displayName = 'FilterByPinned';
